import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import {
  getDeliveryRouteMapFilteredOrders,
  getDeliveryRouteMapFilteredOrdersWithPinnedOrders,
  getDeliveryRouteMapHiddenSelectedOrderIds,
} from './delivery-route-map-model'
import {
  formatDeliveryRouteMapRouteSummary,
  getDeliveryRouteMapHighlightedScrollOrderId,
} from './delivery-route-map-routing'
import { useState } from 'react'

export function DeliveryRouteHiddenSelectionDialog({ onConfirm, onDismiss, pendingFilterChange }) {
  const hiddenSelectedCount = pendingFilterChange?.hiddenSelectedOrderIds.length ?? 0

  return (
    <ConfirmationDialog
      open={Boolean(pendingFilterChange)}
      onOpenChange={(open) => {
        if (!open) {
          onDismiss()
        }
      }}
      trigger={null}
      title={
        hiddenSelectedCount === 1 ? 'Unselect hidden delivery?' : 'Unselect hidden deliveries?'
      }
      description={
        hiddenSelectedCount === 1
          ? 'This filter will hide 1 selected delivery. It will be removed from this route selection.'
          : `This filter will hide ${hiddenSelectedCount} selected deliveries. They will be removed from this route selection.`
      }
      confirmText={hiddenSelectedCount === 1 ? 'Unselect delivery' : 'Unselect deliveries'}
      onConfirm={onConfirm}
    />
  )
}

export function useDeliveryRouteBuilderInteraction({
  initialEnabledBuckets,
  onFilterCommitted,
  onSelectedOrderIdsChange,
  orders,
  pinnedOrderIds = [],
  selectedOrderIds,
  filterableSelectedOrderIds = selectedOrderIds,
}) {
  const [mapEnabledBuckets, setMapEnabledBuckets] = useState(initialEnabledBuckets)
  const [highlightedCardOrderIds, setHighlightedCardOrderIds] = useState([])
  const [highlightedMapOrderIds, setHighlightedMapOrderIds] = useState([])
  const [pendingMapFilterChange, setPendingMapFilterChange] = useState(null)
  const [optimizedRouteSummary, setOptimizedRouteSummary] = useState(null)
  const highlightedMapOrderIdSet = new Set(highlightedMapOrderIds)
  const filteredOrders =
    pinnedOrderIds.length > 0
      ? getDeliveryRouteMapFilteredOrdersWithPinnedOrders(orders, mapEnabledBuckets, pinnedOrderIds)
      : getDeliveryRouteMapFilteredOrders(orders, mapEnabledBuckets)
  const scrollToHighlightedOrderId = getDeliveryRouteMapHighlightedScrollOrderId(
    highlightedMapOrderIds,
    filteredOrders
  )
  const routeMetricSummary = formatDeliveryRouteMapRouteSummary(optimizedRouteSummary)

  const commitMapFilters = (enabledBuckets) => {
    setMapEnabledBuckets(enabledBuckets)
    onFilterCommitted?.()
  }

  const handleMapEnabledBucketsChange = (enabledBuckets) => {
    const hiddenSelectedOrderIds = getDeliveryRouteMapHiddenSelectedOrderIds(
      orders,
      filterableSelectedOrderIds,
      enabledBuckets
    )

    if (hiddenSelectedOrderIds.length === 0) {
      commitMapFilters(enabledBuckets)
      return
    }

    setPendingMapFilterChange({
      enabledBuckets,
      hiddenSelectedOrderIds,
    })
  }

  const confirmPendingMapFilterChange = () => {
    if (!pendingMapFilterChange) {
      return
    }

    const hiddenSelectedOrderIdSet = new Set(pendingMapFilterChange.hiddenSelectedOrderIds)

    onSelectedOrderIdsChange(
      selectedOrderIds.filter((orderId) => !hiddenSelectedOrderIdSet.has(orderId))
    )
    commitMapFilters(pendingMapFilterChange.enabledBuckets)
    setPendingMapFilterChange(null)
  }

  const handleCardHoverChange = (orderId, isHovered) => {
    if (isHovered) {
      setHighlightedCardOrderIds([orderId])
      return
    }

    setHighlightedCardOrderIds((currentOrderIds) =>
      currentOrderIds.includes(orderId) ? [] : currentOrderIds
    )
  }

  const resetMapFilters = (enabledBuckets) => {
    setMapEnabledBuckets(enabledBuckets)
    setPendingMapFilterChange(null)
  }

  return {
    confirmPendingMapFilterChange,
    filteredOrders,
    handleCardHoverChange,
    handleMapEnabledBucketsChange,
    highlightedCardOrderIds,
    highlightedMapOrderIdSet,
    mapEnabledBuckets,
    pendingMapFilterChange,
    resetMapFilters,
    routeMetricSummary,
    scrollToHighlightedOrderId,
    setHighlightedMapOrderIds,
    setOptimizedRouteSummary,
    setPendingMapFilterChange,
  }
}
